import { SeriesLineOptions } from 'highcharts';
import React, { FC } from 'react';
import { useQuery } from 'react-query';
import { ProjectAPI, ProjectProgressPlots } from 'api/project';
import { GENERAL_API_STALE } from 'config/query-config';
import { useTranslation } from 'hooks/use-translation';
import { ErrorContent } from '../error';
import { LoadingContent } from '../loading';
import { DataContent } from './_card-chart';
import { EmptyContent } from './_card-empty';

const Color = {
  PLANNED: 'var(--gray-40)',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  IN_PROGRESS: 'var(--green-40)',
};
const transformDataForHighcharts = (
  data: ProjectProgressPlots,
  title: {
    PLANNED: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    IN_PROGRESS: string;
  },
): { series: Array<Partial<SeriesLineOptions>> } => {
  const series = ['PLANNED', 'IN_PROGRESS'].map((s) => {
    const dataSeries = Object.keys(data.series[s]);

    return {
      enableMouseTracking: false,
      lineWidth: 1,
      marker: {
        enabled: dataSeries.length === 1, // show marker if the data length is 1
      },
      type: 'line' as unknown as 'line',
      name: title[s],
      data: dataSeries.map((d) => {
        return [d, data.series[s][d]];
      }),
      color: Color[s],
    };
  });

  return {
    series,
  };
};

export const Content: FC<{ projectId: number }> = ({ projectId }) => {
  const { data, refetch, status } = useQuery(
    ['project-progress', projectId],
    () => ProjectAPI.getProgress(projectId),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: GENERAL_API_STALE,
    },
  );
  const inProgressText = useTranslation('chart.in_progress');
  const plannedText = useTranslation('chart.planned');

  switch (status) {
    case 'loading':
      return <LoadingContent />;
    case 'success':
      return data && Object.keys(data.series).length > 0 ? (
        <DataContent
          data={transformDataForHighcharts(data, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            IN_PROGRESS: inProgressText,
            PLANNED: plannedText,
          })}
          refetch={refetch}
        />
      ) : (
        <EmptyContent />
      );
    case 'error':
      return <ErrorContent onErrorRefresh={refetch} />;

    case 'idle':
    default:
      return null;
  }
};
